import { Link } from "@tanstack/react-router";
import { CheckCircle, Circle } from "lucide-react"; // Import Lucide React icons
import { useEffect, useState } from "react";
import { useFormContext, useWatch } from "react-hook-form";

import { ui } from "../ui";

interface Props {
	name?: string;
	label?: string;
	showRequirements: boolean;
	showPassword: boolean;
	tabIndex?: number;
	showForgotPassword?: boolean;
}

const Password: React.FC<Props> = ({
	name = "password",
	label = "Password",
	showRequirements = false,
	showPassword = false,
	tabIndex,
	showForgotPassword,
}) => {
	const { control } = useFormContext();
	const [checked, setChecked] = useState<boolean>(false);
	const [isFocused, setIsFocused] = useState<boolean>(false); // Track input focus
	const password = useWatch({ name: "password" }) || "";

	// State to track which requirements are fulfilled
	const [fulfilledRequirements, setFulfilledRequirements] = useState<boolean[]>(
		[],
	);

	// Define password requirements
	const passwordRequirements = [
		{
			description: "Must be at least 8 characters long",
			test: (password: string) => password.length >= 8,
		},
		{
			description: "Must contain a lowercase letter",
			test: (password: string) => /[a-z]/.test(password),
		},
		{
			description: "Must contain an uppercase letter",
			test: (password: string) => /[A-Z]/.test(password),
		},
		{
			description: "Must contain a number",
			test: (password: string) => /[0-9]/.test(password),
		},
		{
			description: "Must contain a special character",
			test: (password: string) => /[!@#$%^&*(),.?":{}|<>]/.test(password),
		},
	];

	useEffect(() => {
		// Only run this effect if showRequirements is true and the component is focused
		if (showRequirements && isFocused) {
			// Check which requirements are fulfilled
			const fulfilled = passwordRequirements.map((req) => req.test(password));
			setFulfilledRequirements(fulfilled);
		}
	}, [password, passwordRequirements, showRequirements, isFocused]);

	return (
		<div className="flex flex-col gap-y-2">
			<ui.FormField
				control={control}
				name={name}
				data-cy={name}
				render={({ field }) => (
					<ui.FormItem>
						<div>
							<div className="flex justify-between">
								<ui.FormLabel>{label}</ui.FormLabel>
								{showForgotPassword !== false && (
									<ui.Button
										asChild
										type="button"
										className="p-0 h-fit"
										size="sm"
										variant="link"
									>
										<Link
											tabIndex={-1}
											to="/forgot-password"
											search={(search) => ({ ...search })}
										>
											Forgot Password
										</Link>
									</ui.Button>
								)}
							</div>
						</div>
						<ui.FormMessage />
						<ui.FormControl>
							<ui.Input
								data-testid="password-input"
								autoComplete="current-password"
								tabIndex={tabIndex ?? 2}
								type={checked ? "text" : "password"}
								onFocus={() => setIsFocused(true)} // Set focus state
								{...field}
							/>
						</ui.FormControl>
					</ui.FormItem>
				)}
			/>
			{showRequirements && isFocused && (
				<div className="bg-gradient-to-br from-pink-100/60 via-indigo-100/80 to-cyan-100/30 opacity-100 p-4 border border-indigo-200 rounded-md transition-opacity duration-300 ease-in-out">
					{fulfilledRequirements.every(Boolean) ? ( // Check if all requirements are fulfilled
						<div className="flex items-center gap-2 p-1">
							<CheckCircle size={20} className="text-emerald-400" />
							<ui.FormDescription>All Good!</ui.FormDescription>
						</div>
					) : (
						passwordRequirements.map((req, index) => (
							<div
								key={req.description}
								className="flex items-center gap-2 p-1"
							>
								{fulfilledRequirements[index] ? (
									<CheckCircle size={20} className="text-emerald-400" />
								) : (
									<Circle size={20} className="text-red-400" />
								)}
								<ui.FormDescription className="text-slate-700">
									{req.description}
								</ui.FormDescription>
							</div>
						))
					)}
				</div>
			)}
			{showPassword && (
				<div className="flex justify-center items-center gap-x-3 mt-2 w-fit">
					<ui.Checkbox
						tabIndex={-1}
						checked={checked as boolean}
						onClick={() => setChecked(!checked)}
					/>
					<ui.Label className="text-sm">Show Password</ui.Label>
				</div>
			)}
		</div>
	);
};

export default Password;
